import { useEffect, useState } from "react";
import { useStore, type Skill } from "@/lib/store";
import { estimateTokenCount } from "@/lib/api";
import { SkillEditor } from "./SkillEditor";

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
}

export function SkillsView({ initialSelectedId, onExit }: { initialSelectedId?: string | null; onExit: () => void }) {
  const skills = useStore((s) => s.skills);
  const refreshSkills = useStore((s) => s.refreshSkills);
  const [selectedId, setSelectedId] = useState<string | null>(initialSelectedId ?? null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    refreshSkills();
  }, [refreshSkills]);

  const selected: Skill | null = selectedId ? skills.find((s) => s.id === selectedId) ?? null : null;

  useEffect(() => {
    setSelectedId(initialSelectedId ?? null);
  }, [initialSelectedId]);

  const exitEditor = () => {
    setCreating(false);
    setSelectedId(null);
  };

  return (
    <div className="fixed inset-y-0 right-0 z-40 flex flex-col bg-background" style={{ left: "var(--sb-w, 0px)" }}>
      <div data-tauri-drag-region className="flex items-center gap-3 border-b px-4 py-3" style={{ paddingTop: "calc(0.75rem + var(--titlebar-h, 0px) + env(safe-area-inset-top))" }}>
        <button onClick={onExit} className="rounded-md p-1.5 text-muted-foreground hover:text-foreground" title="Back to chat">
          <i className="fa-solid fa-arrow-left" />
        </button>
        <h1 className="text-base font-semibold">Skills</h1>
        <span className="text-xs text-muted-foreground">{skills.length}</span>
        <div className="ml-auto">
          <button
            onClick={() => { setCreating(true); setSelectedId(null); }}
            className="rounded-md bg-primary px-2.5 py-1 text-xs text-primary-foreground hover:bg-primary/90"
          >
            <i className="fa-solid fa-plus" /> New skill
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        <div className={`overflow-y-auto border-r ${selected || creating ? "hidden md:block md:w-72" : "w-full md:w-72"}`}>
          {skills.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">
              No skills yet. A skill is a bundle of a system prompt and a set of tools that can be activated for a chat.
            </p>
          ) : (
            <ul>
              {skills.map((s) => {
                const addendumLine = `- ${s.name}${s.description ? ` — ${s.description}` : ""}`;
                const addendumTok = estimateTokenCount(addendumLine);
                const promptChars = s.systemPrompt.length;
                const promptTok = estimateTokenCount(s.systemPrompt);
                return (
                  <li key={s.id}>
                    <button
                      onClick={() => { setSelectedId(s.id); setCreating(false); }}
                      className={`flex w-full flex-col items-start gap-0.5 border-b px-4 py-2.5 text-left hover:bg-muted/50 ${s.id === selectedId ? "bg-muted" : ""}`}
                    >
                      <span className="flex w-full min-w-0 items-center gap-1.5">
                        <span className="truncate text-sm font-medium">{s.name}</span>
                        {!s.autoActivate && (
                          <i className="fa-solid fa-hand shrink-0 text-[10px] text-muted-foreground" title="Manual only — hidden from auto-activation" />
                        )}
                      </span>
                      {s.description && (
                        <span className="line-clamp-2 text-[11px] text-muted-foreground">{s.description}</span>
                      )}
                      <span className="text-[10px] text-muted-foreground">
                        {s.toolNames.length} {s.toolNames.length === 1 ? "tool" : "tools"} · {relativeTime(s.updatedAt)}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        addendum ~{addendumTok} tok · prompt {promptChars} ch / ~{promptTok} tok
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex-1 min-w-0">
          {creating ? (
            <SkillEditor
              skill={null}
              mode="create"
              onSaved={(s) => { setCreating(false); setSelectedId(s.id); }}
              onCancel={exitEditor}
            />
          ) : selected ? (
            <SkillEditor
              key={selected.id}
              skill={selected}
              mode="edit"
              onSaved={() => {}}
              onDeleted={exitEditor}
              onCancel={() => setSelectedId(null)}
            />
          ) : (
            <div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">
              Select a skill to inspect, or create one.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
